import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo } from 'react';
import { getMockEventTargets } from '~/event-reporter/event-targets/useEventTargets';
import { ignoreError } from '~/infra/utils';
import { BusinessPartyTypeEnum } from '~/store/store.model';
const useInternalEventReporter = ()=>useContext(EventReporterContext);
export function useEventReporter() {
    const { gtm, appcues, heap, hubspot } = useInternalEventReporter();
    return useMemo(()=>{
        const sendEvent = (category, action, label, additionalData)=>{
            ignoreError(()=>{
                const eventData = _object_spread_props(_object_spread({}, additionalData), {
                    event_category: category,
                    event_action: action,
                    event_label: label
                });
                const eventName = "".concat(category, "-").concat(action, "-").concat(label);
                gtm.sendEventDeprecated(eventName, eventData);
                heap.sendEventDeprecated(eventName, eventData);
                appcues.sendEventDeprecated(eventName, eventData);
            });
        };
        return {
            reportToGtm: gtm.report,
            sendEventToGtm: gtm.sendEvent,
            reportToHeap: heap.report,
            reportToAppcues: appcues.report,
            reportToHubspot: hubspot.report,
            heapResetIdentity () {
                heap.resetIdentity();
            },
            heapIdentify (userId, userEmail, userProperties) {
                heap.identify(userId, userEmail, userProperties);
            },
            heapAddEventProperties (properties) {
                heap.addEventProperties(properties);
            },
            heapTrack (eventName, data) {
                heap.sendEventDeprecated(eventName, data);
            },
            appcuesIdentify (userId, userEmail, userProperties) {
                appcues.identify(userId, userEmail, userProperties);
            },
            /** @deprecated */ reportButtonClick (buttonName) {
                return sendEvent('button', 'click', buttonName);
            },
            /** @deprecated */ reportOpenNotification (notificationType) {
                return sendEvent('notification', 'open', notificationType);
            },
            /** @deprecated */ reportPopupAction (popupTitle) {
                let isOpen = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : true;
                return sendEvent('popup', isOpen ? 'open' : 'close', popupTitle);
            },
            /** @deprecated */ prepareFormObject (field_name, value) {
                return {
                    field_name,
                    value
                };
            },
            reportPageView (pathname, pageTitle, worksAt) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const eventData = {
                    page_url: pathname,
                    page_title: pageTitle
                };
                if (worksAt != null) {
                    const isVendor = worksAt.type === BusinessPartyTypeEnum.Vendor;
                    eventData.vendor_id = isVendor ? worksAt.id : null;
                    eventData.user_type = isVendor ? 'vendor' : 'customer';
                }
                gtm.sendPageView({
                    page_location: window.location.origin + pathname,
                    page_title: pageTitle
                });
                hubspot.sendPageView(pathname, eventData);
                appcues.sendPageView(pathname, eventData);
            },
            /** @deprecated
             * Migration example
             *
             * Before:
             * reportFormSubmission('user-successfully-sent-first-agreement', null);
             *
             * After:
             * reportToGtm('form-success', 'user-successfully-sent-first-agreement');
             * reportToAppcues('form-success', 'user-successfully-sent-first-agreement');
             * reportToHubspot('user_successfully_sent_first_agreement_');
             * reportToHeap('agreementStudio', 'submit', 'user-successfully-sent-first-agreement');
             */ reportFormSubmission (formName, formData) {
                return sendEvent('form', 'success', formName, {
                    form: formData || {}
                });
            },
            /** @deprecated */ reportProductsFormSubmission (formName, productsArr) {
                return sendEvent('form', 'success', formName, {
                    products: productsArr || []
                });
            }
        };
    }, []);
}
const EventReporterContext = /*#__PURE__*/ createContext(getMockEventTargets());
export function EventReporterProvider(param) {
    let { children, eventTargets } = param;
    return /*#__PURE__*/ _jsx(EventReporterContext.Provider, {
        value: eventTargets,
        children: children
    });
}
